import { lbPerBbl } from "@bl/domain";
import { FlaskConical, TriangleAlert, X } from "lucide-react";
import { useId, useState } from "react";
import { type BrewDetail, send, useCellarSettings, useChange, useLocations } from "../api";
import { type BrewLine, decimalOnly, type Lot, lotKey, shown } from "../brewPulls";
import { ErrorNote } from "../components/ui";
import { showDate, today } from "../format";
import { useNotify } from "../notify";
import { showGravity } from "../recipeFormat";

type Source = BrewLine["repitchFrom"][number];
type Facts = NonNullable<Lot["repitch"]>;
/** A beer in a tank, one of a split batch's tanks apart from the other. */
const sourceKey = (s: Source) => `${s.brewId}|${s.tankId}`;
/** A beer by its recipe and its number, once it has one: "Test Blonde, brew 3". */
export const beerName = (s: { recipeName: string; number: number | null }) =>
  s.number === null ? s.recipeName : `${s.recipeName}, brew ${s.number}`;
/**
 * What a beer's yeast has been through, as Scott judges a repitch (Jam, 2026-10-08: "sometimes you will
 * not want to repitch if the gravity is to high, beer is too hoppy or the yeast has been stagnant for
 * too long without being fed"): each of the three, the ones past the cellar's limits in the warning
 * color, with the limit beside them.
 */
function RepitchFacts({
  facts,
  lead,
  daysLabel,
}: {
  facts: Facts;
  /** What comes first: when it was brewed or harvested. */
  lead: string;
  /** How its days read: "since its brew day", "since it was harvested". */
  daysLabel: string;
}) {
  const { data: cellar } = useCellarSettings();
  const past = (w: Facts["warnings"][number]) => facts.warnings.includes(w);
  const og =
    facts.og === null ? null : `OG ${showGravity(facts.og)}${facts.ogMeasured ? "" : " est."}`;
  const dryHop =
    facts.dryHopGramsPerLiter > 0
      ? `dry hop ${shown(Number(lbPerBbl(facts.dryHopGramsPerLiter.toFixed(6)).toFixed(3)), 2)} lb/bbl`
      : "no dry hop";
  const days = `${facts.days} ${facts.days === 1 ? "day" : "days"} ${daysLabel}`;
  const limit = (w: Facts["warnings"][number]) => {
    if (!cellar) return "";
    if (w === "og" && cellar.repitchMaxOg !== null) {
      return `, over ${showGravity(Number(cellar.repitchMaxOg))}`;
    }
    if (w === "dry_hop" && cellar.repitchMaxDryHop !== null) {
      return `, over ${shown(Number(lbPerBbl(cellar.repitchMaxDryHop).toFixed(3)), 2)}`;
    }
    if (w === "days" && cellar.repitchMaxDays !== null) return `, over ${cellar.repitchMaxDays}`;
    return "";
  };
  const part = (text: string, w: Facts["warnings"][number]) =>
    past(w) ? (
      <span className="repitch-warn">
        <TriangleAlert size={13} aria-hidden /> {text}
        {limit(w)}
      </span>
    ) : (
      <span>{text}</span>
    );
  return (
    <span className="repitch-facts">
      <span>{lead}</span>
      {og ? part(og, "og") : null}
      {part(dryHop, "dry_hop")}
      {part(days, "days")}
    </span>
  );
}
/** Harvested yeast chosen from a brink: the beer it came out of and how long it has waited. */
export function HarvestedFacts({ lot, facts }: { lot: Lot; facts: Facts }) {
  const from = lot.harvestedFrom ? `From ${beerName(lot.harvestedFrom)}` : "Harvested";
  return (
    <div className="repitch-lot">
      <RepitchFacts
        facts={facts}
        lead={lot.receivedOn ? `${from}, harvested ${showDate(lot.receivedOn)}` : from}
        daysLabel="since it was harvested"
      />
    </div>
  );
}
/**
 * Repitching tank to tank (Scott's Jam, 2026-10-08: "once a yeast is used once it can be repitched
 * from tank to tank"): the beers of the strain in a fermenter on brew day, those past none of the
 * cellar's limits first. Weighing one records its harvest from that beer, a generation on, and pitches
 * all of it here, so its generation and cost carry on as any harvest's do.
 */
export function RepitchFrom({
  brew: b,
  line,
  ready,
  onPitched,
}: {
  brew: BrewDetail;
  line: BrewLine;
  ready: boolean;
  onPitched: (lotKey: string, amount: string) => void;
}) {
  const clean = line.repitchFrom.find((s) => s.warnings.length === 0);
  const [open, setOpen] = useState<string | null>(ready && clean ? sourceKey(clean) : null);
  return (
    <>
      <div className="repitch-group">In a fermenter now</div>
      {line.repitchFrom.map((s) => (
        <RepitchSource
          key={sourceKey(s)}
          brew={b}
          line={line}
          source={s}
          open={open === sourceKey(s)}
          onOpen={() => setOpen(sourceKey(s))}
          onClose={() => setOpen(null)}
          onPitched={(lotKey, amount) => {
            setOpen(null);
            onPitched(lotKey, amount);
          }}
        />
      ))}
    </>
  );
}
function RepitchSource({
  brew: b,
  line,
  source: s,
  open,
  onOpen,
  onClose,
  onPitched,
}: {
  brew: BrewDetail;
  line: BrewLine;
  source: Source;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onPitched: (lotKey: string, amount: string) => void;
}) {
  const id = useId();
  const notify = useNotify();
  const { data: locations = [] } = useLocations();
  const [pounds, setPounds] = useState("");
  const [generation, setGeneration] = useState("");
  const place = s.locationId ?? locations[0]?.id ?? "";
  const g = s.generation ?? (generation ? Number(generation) : null);
  const pitch = useChange(
    (_: undefined, key) =>
      send.harvest(
        {
          brewId: s.brewId,
          ...(line.item ? { itemId: line.item.id } : {}),
          ...(s.generation === null && generation ? { generation: Number(generation) } : {}),
          locationId: place,
          quantity: { value: pounds, unit: "lb" },
          note: `Harvested from ${s.tankName} to pitch into brew ${b.number ?? ""} of ${b.recipe.name}`,
        },
        key,
      ),
    {
      // Once the brew is read again with the harvest among its lots, it is what this line pitches.
      onSuccess: (done) => {
        if (done.lot) onPitched(lotKey({ lotId: done.lot.id, locationId: place }), pounds);
        notify(`Harvested ${pounds} lb from ${s.tankName} to pitch${g ? `, G${g}` : ""}`);
      },
    },
  );
  const canPitch =
    Number(pounds) > 0 && place !== "" && (s.generation !== null || generation !== "");
  const brewedToday = b.brewedOn === today();
  return (
    <div className={`repitch-source ${s.warnings.length ? "warned" : ""}`}>
      <div className="repitch-head">
        <span>
          <strong>{s.tankName}</strong> · {beerName(s)}
        </span>
        {s.generation !== null ? <span className="num repitch-gen">G{s.generation}</span> : null}
      </div>
      <RepitchFacts
        facts={s}
        lead={`Brewed ${showDate(s.brewedOn)}`}
        daysLabel={brewedToday ? "ago" : "before this brew"}
      />
      {open ? (
        <form
          className="repitch-form"
          onSubmit={(ev) => {
            ev.preventDefault();
            if (canPitch) pitch.mutate(undefined);
          }}
        >
          <div className="pull-amount">
            <span className="input-unit">
              <input
                id={id}
                className="input num"
                inputMode="decimal"
                enterKeyHint="done"
                autoComplete="off"
                aria-label={`How much yeast from ${s.tankName}, in lb`}
                placeholder="Weight"
                value={pounds}
                onChange={(ev) => setPounds(decimalOnly(ev.target.value))}
              />
              <span className="unit">lb</span>
            </span>
            <button
              type="button"
              className="icon-button"
              aria-label="Don't pitch from this tank"
              title="Don't pitch from this tank"
              onClick={onClose}
            >
              <X size={20} />
            </button>
          </div>
          {s.generation === null ? (
            <label className="repitch-generation">
              <span className="muted">
                Its yeast was not in inventory, so say its generation: how many beers it will have
                fermented once harvested.
              </span>
              <input
                className="input num"
                inputMode="numeric"
                autoComplete="off"
                aria-label="Generation"
                value={generation}
                onChange={(ev) => setGeneration(ev.target.value.replace(/\D/g, "").slice(0, 2))}
              />
            </label>
          ) : null}
          <button type="submit" className="button primary" disabled={!canPitch || pitch.isPending}>
            <FlaskConical size={16} /> Pitch from {s.tankName}
          </button>
          <p className="muted repitch-note">
            Records {g ? `a G${g}` : "a"} harvest from {beerName(s)} and pitches all of it here.
          </p>
          <ErrorNote error={pitch.error} />
        </form>
      ) : (
        <div className="pull-fixes">
          <button type="button" className="button" onClick={onOpen}>
            <FlaskConical size={16} /> Pitch from {s.tankName}
          </button>
        </div>
      )}
    </div>
  );
}
